import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { Loader2, CreditCard, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../hooks/useLanguage';
import { paymentTrace } from '../lib/paymentTrace';

const StripePaymentForm = ({ amount, bookingId, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useLanguage();
  const [processingPhase, setProcessingPhase] = useState(null);
  const [elementReady, setElementReady] = useState(false);

  const isProcessing = processingPhase !== null;
  const overlayTitle =
    processingPhase === 'confirmation'
      ? t('booking.paymentOverlayConfirmTitle')
      : t('booking.paymentOverlayTitle');
  const overlayMessage =
    processingPhase === 'confirmation'
      ? t('booking.paymentOverlayConfirmMessage')
      : t('booking.paymentOverlayMessage');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || isProcessing) {
      return;
    }

    setProcessingPhase('payment');
    paymentTrace('stripe.confirmPayment.start', { booking_id: bookingId, amount });

    try {
      const returnUrl = new URL('/booking-confirmation', window.location.origin);
      if (bookingId) {
        returnUrl.searchParams.set('booking_id', bookingId);
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl.toString(),
        },
        redirect: 'if_required',
      });

      if (error) {
        paymentTrace('stripe.confirmPayment.error', {
          booking_id: bookingId,
          code: error.code,
          type: error.type,
          message: error.message,
        });
        toast.error(error.message || 'Payment failed');
        onError(error);
        setProcessingPhase(null);
        return;
      }

      paymentTrace('stripe.confirmPayment.result', {
        booking_id: bookingId,
        status: paymentIntent?.status,
        payment_intent_id: paymentIntent?.id,
        redirect_required: !paymentIntent,
      });

      if (
        paymentIntent &&
        (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')
      ) {
        setProcessingPhase('confirmation');
        paymentTrace('stripe.onSuccess.calling', {
          booking_id: bookingId,
          payment_intent_id: paymentIntent.id,
          status: paymentIntent.status,
        });
        await onSuccess(paymentIntent);
        return;
      }

      paymentTrace('stripe.confirmPayment.incomplete', {
        booking_id: bookingId,
        status: paymentIntent?.status || 'none',
      });
      setProcessingPhase(null);
    } catch (err) {
      paymentTrace('stripe.confirmPayment.exception', {
        booking_id: bookingId,
        message: err?.message,
      });
      toast.error('An error occurred during payment');
      onError(err);
      setProcessingPhase(null);
    }
  };

  const overlay =
    isProcessing &&
    createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-primary-950/75 backdrop-blur-sm px-4"
        role="alertdialog"
        aria-modal="true"
        aria-busy="true"
        aria-labelledby="payment-processing-title"
        aria-describedby="payment-processing-message"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center border border-primary-100">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-50 mb-5">
            <Loader2 className="w-9 h-9 animate-spin text-primary-600" />
          </div>
          <h3
            id="payment-processing-title"
            className="text-xl font-bold text-primary-950 mb-2"
          >
            {overlayTitle}
          </h3>
          <p id="payment-processing-message" className="text-gray-600 text-sm leading-relaxed">
            {overlayMessage}
          </p>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      {overlay}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gradient-to-r from-primary-50 to-accent-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary-600" />
              <span className="font-semibold text-gray-900">Total to pay</span>
            </div>
            <span className="text-2xl font-bold text-primary-600">{amount}€</span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 relative min-h-[120px]">
          {!elementReady && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('booking.processing')}
            </div>
          )}
          <PaymentElement
            options={{
              layout: 'tabs',
              paymentMethodOrder: ['card'],
            }}
            onReady={() => {
              setElementReady(true);
              paymentTrace('stripe.paymentElement.ready', { booking_id: bookingId });
            }}
            onLoadError={(event) => {
              const message =
                event?.error?.message || 'Payment form failed to load';
              paymentTrace('stripe.paymentElement.load_error', {
                booking_id: bookingId,
                message,
                code: event?.error?.code,
              });
              toast.error(message);
              onError(event?.error || new Error(message));
            }}
          />
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
          <Lock className="w-4 h-4" />
          <span>Secure payment powered by Stripe. Your payment information is encrypted.</span>
        </div>

        <Button
          type="submit"
          disabled={!stripe || !elementReady || isProcessing}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white py-6 text-lg font-semibold rounded-lg"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {processingPhase === 'confirmation'
                ? t('booking.paymentOverlayConfirmTitle')
                : t('booking.processing')}
            </>
          ) : (
            <>
              <Lock className="w-5 h-5 mr-2" />
              Pay {amount}€
            </>
          )}
        </Button>
      </form>
    </>
  );
};

export default StripePaymentForm;
